'use client'

import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'

import {
  buildLotHistoryEntries,
  HistoryChange,
  LotAssertionHistoryItem,
  LotHistoryEntry,
} from '~/app/accounting/lot-assertion-history'
import { LotRow } from '~/app/accounting/lot-rows'
import {
  CurrencyAmount,
  LoadingSpinner,
  Timestamp,
  tokenBalanceFormatters,
  UserDisplay,
} from '~/components/component-library'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrg } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress } from '~/types'
import { getNumberFromBigNumber } from '~/utils/numbers'

const FIELD_LABELS: Record<HistoryChange['field'], string> = {
  unitPrice: 'Lot price',
  acquisitionTime: 'Date received',
  quantity: 'Amount',
  originLotId: 'Origin lot',
}

const ChangeValue = ({
  field,
  value,
  lot,
}: {
  field: HistoryChange['field']
  value: string | null
  lot: LotRow
}) => {
  const [tz] = useAccountingTimezone()
  if (value === null) return <span className="text-secondary">Derived</span>
  switch (field) {
    case 'unitPrice':
      return (
        <CurrencyAmount layout="standard" size="small" value={Number(value)} />
      )
    case 'acquisitionTime':
      return <Timestamp timestamp={value} type="absolute" abbreviate tz={tz} />
    case 'quantity':
      return (
        <span>
          {tokenBalanceFormatters.formatAbbreviatedTokenAmount(
            getNumberFromBigNumber(BigInt(value), lot.decimals),
            { address: lot.tokenAddress as IAddress, chainId: lot.chainId },
          )}{' '}
          {lot.symbol}
        </span>
      )
    case 'originLotId':
      return <span className="truncate font-mono text-xs">{value}</span>
  }
}

const HistoryEntry = ({
  entry,
  lot,
}: {
  entry: LotHistoryEntry
  lot: LotRow
}) => {
  const [tz] = useAccountingTimezone()
  return (
    <div className="flex flex-col gap-1.5 px-4 py-3 text-sm">
      {/* Author and time read as one phrase, so they share the title's line
          rather than wrapping onto a second row. */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{entry.title}</span>
        <span className="flex shrink-0 items-baseline gap-1 text-xs text-secondary">
          {entry.createdBy ? (
            <>
              by{' '}
              <UserDisplay
                user={{
                  id: entry.createdBy.id,
                  email: entry.createdBy.email,
                  displayName: entry.createdBy.displayName ?? undefined,
                  profilePhotoUrl: entry.createdBy.profilePhotoUrl ?? undefined,
                }}
                layout="inline-text"
              />
            </>
          ) : (
            'by a removed member'
          )}
          <Timestamp
            timestamp={entry.createdAt}
            type="relative"
            abbreviate
            showTooltip
            tz={tz}
          />
        </span>
      </div>
      {entry.changes.map((change) => (
        <div
          key={change.field}
          className="flex items-center justify-between gap-2"
        >
          <span className="text-secondary">{FIELD_LABELS[change.field]}</span>
          <span className="flex min-w-0 items-center gap-1.5">
            <ChangeValue field={change.field} value={change.from} lot={lot} />
            <ArrowRightIcon className="h-3 w-3 shrink-0 text-secondary" />
            <ChangeValue field={change.field} value={change.to} lot={lot} />
          </span>
        </div>
      ))}
    </div>
  )
}

export const LotAssertionHistory = ({ lot }: { lot: LotRow }) => {
  const { serverGetRequest } = useServerRequests()
  const { org } = useActiveOrg()

  const {
    data: items,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEYS.ORG_ACCOUNTING_LOT_ASSERTIONS, org?.id, lot.id],
    queryFn: async () => {
      if (!org?.id) throw new Error('Org ID required')

      const response = await serverGetRequest<{
        assertions: LotAssertionHistoryItem[]
      }>({
        path: `/api/org/accounting/lots/${encodeURIComponent(lot.id)}/assertions`,
        params: { orgId: org.id },
        errorMessage: 'Failed to fetch lot history',
      })
      if (!response) throw new Error('Failed to fetch lot history')
      return response.data.assertions
    },
    enabled: !!org?.id,
  })

  const entries = useMemo(() => buildLotHistoryEntries(items ?? []), [items])

  return (
    <div className="border-t border-gray-200 pt-4 dark:border-gray-800">
      <div className="mb-2 text-sm font-medium">History</div>
      {isLoading ? (
        <div className="flex min-h-16 items-center justify-center">
          <LoadingSpinner size="lg" thickness="normal" variant="default" />
        </div>
      ) : isError ? (
        <div className="text-sm text-secondary">
          Unable to load this lot&apos;s history right now
        </div>
      ) : entries.length ? (
        <div className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
          {entries.map((entry) => (
            <HistoryEntry key={entry.id} entry={entry} lot={lot} />
          ))}
        </div>
      ) : (
        <div className="text-sm text-secondary">
          No edits recorded for this lot
        </div>
      )}
    </div>
  )
}
